import React, { useState } from "react";
import Dropdown from "./dropdown";
import { MultiSelectDropdownProps } from "./types";
import Toggle from "../toggle";

const MultiSelectDropdown = <OptionType, SelectionType>({
  label,
  onOptionToggle,
  isLoading,
  options = [],
  selections = [],
  renderOption,
  onSearchTermChange,
  mapOptionToSelection,
  className,
  enableSearch = true,
  showSelectAll = false,
  onSelectAll,
}: MultiSelectDropdownProps<OptionType, SelectionType>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const areAllSelected = options.length > 0 && selections.length === options.length;

  const handleOptionClick = (option: OptionType, event: React.MouseEvent) => {
    if ((event.target as Element).tagName !== 'INPUT') {
      onOptionToggle(option);
    }
  };

  const handleSearchTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    onSearchTermChange(newSearchTerm);
  };

  return (
    <Dropdown label={label} isOpen={isOpen} setIsOpen={setIsOpen} className={className}>
      <div className="max-h-[14rem] overflow-auto">
        {enableSearch && (
          <div className="px-4 py-2" onKeyDown={e => e.stopPropagation()}>
            <input
              type="text"
              className="form-input mt-1 block w-full rounded text-gray-700"
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearchTermChange}
            />
          </div>
        )}
        {showSelectAll && onSelectAll && (
          <div className="px-4 py-2">
            <Toggle
              value={areAllSelected}
              onChange={onSelectAll}
              label={areAllSelected ? "Hide all" : "Show all"}
              labelPosition="right"
              size="small"
              layout="horizontal"
              labelClassName="text-xs text-gray-600"
              className="flex-shrink-0"
            />
          </div>
        )}
        {!options || isLoading ? (
          <div className="px-4 py-2">Loading...</div>
        ) : (
          options.map((option) => (
            <div
              key={String(mapOptionToSelection(option))}
              onClick={(e) => handleOptionClick(option, e)}
              className="px-4 py-2 hover:bg-blue-300 hover:text-white flex w-full items-center justify-between text-sm cursor-pointer"
            >
              <span className="flex-grow break-words pr-2 text-left">
                {renderOption(option)}
              </span>
              <input
                type="checkbox"
                checked={selections.includes(mapOptionToSelection(option))}
                onChange={() => onOptionToggle(option)}
                className="form-checkbox"
                onClick={(e) => e.stopPropagation()}
                aria-label={renderOption(option)}
              />
            </div>
          ))
        )}
      </div>
    </Dropdown>
  );
};

export default MultiSelectDropdown;
